import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@telegram-apps/telegram-ui";
import { BookInfo } from "../../handlers/types";
import ApiService from "../../handlers/apiService";
import { t } from "../../utils/i18n/index";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./MagicBooks.css";

interface BookCardProps {
  book: BookInfo;
  onPurchaseBook: (bookId: string) => void;
  isPurchasing?: boolean;
  userBalance: number; // Add userBalance prop
}

// Memoize BookCard to prevent unnecessary rerenders
const BookCard = React.memo(function BookCard({
  book,
  onPurchaseBook,
  isPurchasing,
  userBalance,
}: BookCardProps) {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);

  // Add affordability check
  const canAfford = userBalance >= book.price;

  const handlePurchaseClick = useCallback(() => {
    if (!book.isOwned && !isPurchasing && canAfford) {
      onPurchaseBook(book.id);
    }
  }, [book.id, book.isOwned, onPurchaseBook, isPurchasing, canAfford]);

  const bookTitle = book.nameKey ? t(book.nameKey) : book.id;

  return (
    <div className={`book-card book-card--${book.rarity.toLowerCase()}`}>
      {/* Book Title */}
      <h3 className="book-card__title">{bookTitle}</h3>

      {/* Image and Details Container */}
      <div className="book-card__content">
        <div className="book-card__image-container">
          <img
            src={`/assets/img/books/${book.imageKey}.avif`}
            alt={bookTitle}
            className="book-card__image"
          />
        </div>

        <div className="book-card__details">
          {/* Rarity */}
          <div className="book-card__details-content">
            <div
              className={`book-card__rarity book-card__rarity--${book.rarity.toLowerCase()}`}
            >
              {t(`looks.rarity.${book.rarity.toLowerCase()}`, {
                defaultValue: book.rarity,
              })}
            </div>

            {/* Mana Generation */}
            <div className="book-card__mana">
              +{book.manaGenerationIncrement} {t("magicBooks.manaPerDay")}
            </div>
          </div>

          {/* Purchase Button or No Button for Owned */}
          {!book.isOwned && (
            <Button
              size="s"
              mode="filled"
              className={`book-card__purchase-button ${
                !canAfford ? "book-card__purchase-button--disabled" : ""
              }`}
              onClick={handlePurchaseClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              disabled={isPurchasing || !canAfford}
            >
              <span className="book-card__purchase-button-content">
                {t("magicBooks.buy")} {book.price}{" "}
                <img
                  src="assets/img/ui/coin.webp"
                  alt="coin"
                  className="book-card__coin-icon"
                />
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

interface MagicBooksProps {
  userBalance?: number; // Add optional userBalance prop
  onBalanceUpdate?: (newBalance: number) => void; // Add callback for balance updates
}

export function MagicBooks({
  userBalance = 0,
  onBalanceUpdate,
}: MagicBooksProps): JSX.Element {
  const { t } = useI18n();
  // Add prop with default
  const [books, setBooks] = useState<BookInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const apiService = ApiService.getInstance();

  // Memoize fetchBooks to prevent recreation on every render
  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const booksData = await apiService.api.booksList();
      setBooks(booksData.books || []);
    } catch (err) {
      console.error("Failed to fetch books:", err);
      setError(t("magicBooks.errorLoadingBooks"));
    } finally {
      setLoading(false);
    }
  }, [apiService, t]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Optimized purchase handler that updates only the specific book
  const handlePurchaseBook = useCallback(
    async (bookId: string) => {
      try {
        setPurchasing(bookId);
        setError(null);

        // Find the book to get its price
        const book = books.find((b) => b.id === bookId);
        if (!book) {
          setError(t("magicBooks.errorPurchaseBook"));
          return;
        }

        const response = await apiService.api.booksBuyCreate(bookId);

        if (response.success) {
          // Update the specific book's ownership status
          setBooks((prevBooks) =>
            prevBooks.map((book) =>
              book.id === bookId ? { ...book, isOwned: true } : book
            )
          );

          // Update the balance by subtracting the book price
          const newBalance = userBalance - book.price;
          onBalanceUpdate?.(newBalance);
        } else {
          setError(t("magicBooks.errorPurchaseBook"));
        }
      } catch (err) {
        console.error("Failed to purchase book:", err);
        setError(t("magicBooks.errorPurchaseBook"));
      } finally {
        setPurchasing(null);
      }
    },
    [apiService, books, userBalance, onBalanceUpdate, t]
  );

  const handleNavigateToShop = useCallback(() => {
    navigate("/shop");
  }, [navigate]);

  // Memoize the books list to prevent unnecessary re-renders
  const { ownedBooks, newBooks } = useMemo(() => {
    const owned = books.filter((book) => book.isOwned);
    const notOwned = books.filter((book) => !book.isOwned);

    return {
      ownedBooks: owned,
      newBooks: notOwned,
    };
  }, [books]);

  const renderSectionHeader = useCallback(
    (title: string) => (
      <div className="magic-books__section-header">
        <h2 className="magic-books__section-title">{title}</h2>
      </div>
    ),
    []
  );

  const renderBookCards = useCallback(
    (sectionBooks: BookInfo[]) =>
      sectionBooks.map((book) => (
        <BookCard
          key={book.id}
          book={book}
          onPurchaseBook={handlePurchaseBook}
          isPurchasing={purchasing === book.id}
          userBalance={userBalance}
        />
      )),
    [handlePurchaseBook, purchasing, userBalance]
  );

  return (
    <div className="magic-books" ref={scrollContainerRef}>
      {/* Header */}
      <div className="magic-books__header">
        <h1 className="magic-books__title">{t("magicBooks.title")}</h1>
        <p className="magic-books__subtitle">{t("magicBooks.subtitle")}</p>
        {error && <div className="magic-books__error">{error}</div>}
      </div>

      {/* Cards Container */}
      <div className="magic-books__cards-container">
        {/* Coins Widget */}
        <div className="magic-books__coins-widget">
          <img
            src="assets/img/ui/coin.webp"
            alt="Coins"
            className="magic-books__coins-icon"
          />
          <div className="magic-books__coins-value-container">
            <div className="magic-books__coins-value">{userBalance}</div>
            <div className="magic-books__coins-label">{t("common.coins")}</div>
          </div>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="magic-books__add-coins-icon"
            onClick={handleNavigateToShop}
          >
            <path
              d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="magic-books__cards">
          {/* Loading State */}
          {loading && (
            <div className="magic-books__loading">{t("common.loading")}...</div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="magic-books__error">{error}</div>
          )}

          {/* No Books State */}
          {!loading && !error && books.length === 0 && (
            <div className="magic-books__no-books">
              {t("magicBooks.noBooksAvailable")}
            </div>
          )}

          {/* Books with Sections */}
          {!loading && !error && books.length > 0 && (
            <>
              {/* Owned Books Section */}
              {ownedBooks.length > 0 && (
                <>
                  {renderSectionHeader(t("magicBooks.sections.owned"))}
                  {renderBookCards(ownedBooks)}
                </>
              )}

              {/* New Books Section */}
              {newBooks.length > 0 && (
                <>
                  {renderSectionHeader(t("magicBooks.sections.new"))}
                  {renderBookCards(newBooks)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
